import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import { useTheme } from 'styled-components/native'

import { useIsScreenScrolled } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useIsScreenScrolled'
import { headerNavItemData } from '@lyrahealth-inc/shared-app-logic/src/features/header/constants'
import { HEADER_NAV_ITEM } from '@lyrahealth-inc/shared-app-logic/src/features/header/types'

import { HeaderProps } from './Header'
import { CaregivingIcon } from '../../atoms/icons/CaregivingIcon'
import { EssentialsIcon } from '../../atoms/icons/EssentialsIcon'
import { HomeIcon } from '../../atoms/icons/HomeIcon'
import { getTabItemIcon } from '../../utils/tabItemUtils'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { GlobalHeader } from '../globalHeader/GlobalHeader'
import { LightHomeToolbar } from '../lightHomeToolbar/LightHomeToolbar'

/** Main header for the Lyra app (web) */
export const Header: FunctionComponent<HeaderProps> = ({
  isInternationalUser,
  shouldShowCareNavigatorHeaderEntrypoint,
  careNavigatorModal,
  careNavigatorEntryPoint,
  headerNavMenuProps,
  mainNavItemsProps,
  currentPathname,
  currentScreen,
  onLogoPressed,
  shouldShowNavMenu = true,
  shouldShowBack,
  onBackPressed,
  title,
  showMessages,
  messagesPopover,
  onMessagesPressed,
  unreadMessages,
  favoritedProvidersEntrypointNavProps,
  shouldShowLoginAndSignUpButtons,
  onLoginPressed,
  onSignUpPressed,
  shouldShowReferral,
  onReferralPressed,
  isTransparentBackground,
  shouldOverlapPageContent,
  shouldShowLanguageSelector,
  onLanguageSelectorGetHelpPress,
  beforeLaunch,
  showAICoachIcon,
  shouldShowSearch,
  onSearchPressed,
  isBlurBackground,
  logoFillColor,
  useGlobalHeader = false,
  renderBottomNavInPortal = false,
}) => {
  const { formatMessage } = useIntl()
  const { isAuroraTheme } = useTheme() as ThemeType
  const { isScrolled } = useIsScreenScrolled()

  const navItems = [
    {
      id: HEADER_NAV_ITEM.HOME,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.HOME].label),
      onPressItem: mainNavItemsProps?.handleHomePress,
      activePathnames: headerNavItemData[HEADER_NAV_ITEM.HOME].activePathnames,
      activeScreens: headerNavItemData[HEADER_NAV_ITEM.HOME].activeScreenNames,
      condition: mainNavItemsProps?.shouldShowHome,
      icon: (isActive: boolean) => getTabItemIcon({ isActive, IconComponent: HomeIcon, testID: 'HomeIcon' }),
      floatingIcon: (iconColor: string) => <HomeIcon fillColor={iconColor} testID={tID('HomeIcon')} />,
    },
    {
      id: HEADER_NAV_ITEM.CARE,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.CARE].label),
      onPressItem: mainNavItemsProps?.handleCarePress,
      activePathnames: headerNavItemData[HEADER_NAV_ITEM.CARE].activePathnames,
      activeScreens: headerNavItemData[HEADER_NAV_ITEM.CARE].activeScreenNames,
      condition: mainNavItemsProps?.shouldShowCare,
      icon: (isActive: boolean) =>
        getTabItemIcon({ isActive, IconComponent: CaregivingIcon, testID: 'CaregivingIcon' }),
      floatingIcon: (iconColor: string) => <CaregivingIcon fillColor={iconColor} testID={tID('CaregivingIcon')} />,
    },
    {
      id: HEADER_NAV_ITEM.ESSENTIALS,
      text: formatMessage(headerNavItemData[HEADER_NAV_ITEM.ESSENTIALS].label),
      onPressItem: mainNavItemsProps?.handleEssentialsPress,
      activePathnames: headerNavItemData[HEADER_NAV_ITEM.ESSENTIALS].activePathnames,
      activeScreens: headerNavItemData[HEADER_NAV_ITEM.ESSENTIALS].activeScreenNames,
      condition: mainNavItemsProps?.shouldShowEssentials,
      icon: (isActive: boolean) =>
        getTabItemIcon({ isActive, IconComponent: EssentialsIcon, testID: 'EssentialsIcon' }),
      floatingIcon: (iconColor: string) => <EssentialsIcon fillColor={iconColor} testID={tID('EssentialsIcon')} />,
    },
  ]

  if (isAuroraTheme && useGlobalHeader) {
    return (
      <GlobalHeader
        isScrolled={isScrolled}
        navItems={navItems}
        currentPathname={currentPathname}
        currentScreen={currentScreen}
        onLogoPressed={onLogoPressed}
        onBackPressed={shouldShowBack ? onBackPressed : undefined}
        title={title}
        showMessages={showMessages}
        messagesPopover={messagesPopover}
        onMessagesPressed={onMessagesPressed}
        unreadMessages={unreadMessages}
        showAICoachIcon={showAICoachIcon}
        shouldShowSearch={shouldShowSearch}
        onSearchPressed={onSearchPressed}
        shouldShowNavMenu={shouldShowNavMenu}
        headerNavMenuProps={headerNavMenuProps}
        shouldShowCareNavigatorHeaderEntrypoint={shouldShowCareNavigatorHeaderEntrypoint}
        careNavigatorEntryPoint={careNavigatorEntryPoint}
        careNavigatorModal={careNavigatorModal}
        favoritedProvidersEntrypointNavProps={favoritedProvidersEntrypointNavProps}
        logoFillColor={logoFillColor}
        renderBottomNavInPortal={renderBottomNavInPortal}
      />
    )
  }

  return (
    <LightHomeToolbar
      testID={tID('AppHeader')}
      isInternational={isInternationalUser}
      shouldShowCareNavigatorHeaderEntrypoint={shouldShowCareNavigatorHeaderEntrypoint}
      careNavigatorModal={careNavigatorModal}
      careNavigatorEntryPoint={careNavigatorEntryPoint}
      showNavMenu={shouldShowNavMenu}
      headerNavMenuProps={headerNavMenuProps}
      navItems={navItems}
      currentPathname={currentPathname}
      currentScreen={currentScreen}
      onLogoPressed={onLogoPressed}
      showBack={shouldShowBack}
      onBackPressed={onBackPressed}
      title={title}
      showMessages={showMessages}
      onMessagesPressed={onMessagesPressed}
      messagesPopover={messagesPopover}
      unreadMessages={unreadMessages}
      favoritedProvidersEntrypointNavProps={favoritedProvidersEntrypointNavProps}
      shouldShowLoginAndSignUpButtons={shouldShowLoginAndSignUpButtons}
      onLoginPressed={onLoginPressed}
      onSignUpPressed={onSignUpPressed}
      shouldShowReferral={shouldShowReferral}
      onReferralPressed={onReferralPressed}
      isTransparentBackground={isTransparentBackground || isAuroraTheme}
      shouldOverlapPageContent={shouldOverlapPageContent}
      shouldShowLanguageSelector={shouldShowLanguageSelector}
      onLanguageSelectorGetHelpPress={onLanguageSelectorGetHelpPress}
      beforeLaunch={beforeLaunch}
      showAICoachIcon={showAICoachIcon}
      shouldShowSearch={shouldShowSearch}
      onSearchPressed={onSearchPressed}
      isBlurBackground={isBlurBackground}
      logoFillColor={logoFillColor}
    />
  )
}
